import React, { useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { apiService } from '../../services/api';
import { colors, spacing, typography, withAlpha } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useAppStore } from '../../store/appStore';
import type { DocumentLanguageChoice, DocumentType } from '../../utils/documentTypes';

interface Props {
  type: DocumentType;
  templateId?: string;
  onTemplate: (id: string | undefined) => void;
  language: DocumentLanguageChoice;
  onLanguage: (language: DocumentLanguageChoice) => void;
  onReadyChange?: (ready: boolean) => void;
  disabled?: boolean;
}

/** Start with the business default. Extra layouts are revealed only when the user asks. */
export const DocumentTemplatePicker: React.FC<Props> = ({ type, templateId, onTemplate, language, onLanguage, onReadyChange, disabled = false }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const businessId = useAppStore(s => String(s.currentBusiness?._id || (s.currentBusiness as any)?.id || ''));
  const scope = `${businessId}:${type}`;
  const [loadedScope, setLoadedScope] = useState('');
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setTemplates([]);
    setError(false);
    setExpanded(false);
    void apiService.getQuotationTemplates(type).then(res => {
      if (!alive) return;
      if (res.statusCode !== 1000 || !Array.isArray(res.data)) throw Error('Templates unavailable');
      setTemplates(res.data);
      setLoadedScope(scope);
      setLoading(false);
    }).catch(() => {
      if (!alive) return;
      setLoading(false);
      setLoadedScope(scope);
      setError(true);
    });
    return () => { alive = false; };
  }, [type, businessId, attempt]);

  const pending = loading || loadedScope !== scope;
  const selectionMissing = !pending && !error && !!templateId && !templates.some(item => item._id === templateId);
  useEffect(() => { onReadyChange?.(!pending && !error && !selectionMissing); }, [pending, error, selectionMissing, onReadyChange]);

  const defaultTemplate = templates.find(item => item.isDefault);
  const selected = templateId ? templates.find(item => item._id === templateId) : defaultTemplate;
  const selectedName = selected?.name || t('documents.builtIn');
  const choose = (id?: string) => { onTemplate(id); setExpanded(false); };

  return (
    <View style={[styles.wrap, disabled && { opacity: 0.6 }]}>
      <Text style={[styles.label, { textAlign: rtl.textStart }]}>{t('documents.template')}</Text>
      {pending ? <ActivityIndicator size="small" color={colors.primary as string} /> : error || selectionMissing ? (
        <View style={styles.errorBox}>
          <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t(selectionMissing ? 'documents.picker.selectionMissing' : 'documents.picker.loadError')}</Text>
          <TouchableOpacity disabled={disabled} onPress={() => selectionMissing ? onTemplate(undefined) : setAttempt(n => n + 1)} accessibilityRole="button">
            <Text style={styles.link}>{t(selectionMissing ? 'documents.picker.useDefault' : 'common.retry')}</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <>
          <View style={[styles.selection, { flexDirection: rtl.rowDirection }]}>
            <Ionicons name={templateId ? 'document-text-outline' : 'star-outline'} size={20} color={colors.primary as string} />
            <View style={styles.info}>
              <Text style={[styles.selectionName, { textAlign: rtl.textStart }]}>{selectedName}</Text>
              <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{templateId ? t('documents.picker.thisDocumentOnly') : t('documents.picker.businessDefault')}</Text>
            </View>
            {!!templates.length && (
              <TouchableOpacity disabled={disabled} onPress={() => setExpanded(v => !v)} accessibilityRole="button" accessibilityState={{ expanded, disabled }}>
                <Text style={styles.link}>{expanded ? t('common.close') : t('documents.picker.change')}</Text>
              </TouchableOpacity>
            )}
          </View>
          {expanded && (
            <View style={styles.choices}>
              {[{ _id: undefined, name: t('documents.defaultTemplate', { name: defaultTemplate?.name || t('documents.builtIn') }) }, ...templates].map((item, index) => {
                const active = templateId === item._id;
                return (
                  <TouchableOpacity disabled={disabled} key={item._id || 'default'} onPress={() => choose(item._id)} accessibilityRole="radio" accessibilityState={{ checked: active, disabled }} style={[styles.option, { flexDirection: rtl.rowDirection }, active && styles.active]}>
                    <Ionicons name={active ? 'radio-button-on' : 'radio-button-off'} size={18} color={(active ? colors.primary : colors.text.secondary) as string} />
                    <Text style={[styles.optionText, { textAlign: rtl.textStart }]}>{item.name}</Text>
                    {index > 0 && item.isDefault && <Ionicons name="star" size={13} color={colors.primary as string} />}
                  </TouchableOpacity>
                );
              })}
            </View>
          )}
        </>
      )}
      <Text style={[styles.label, { textAlign: rtl.textStart }]}>{t('documents.language')}</Text>
      <View style={[styles.languages, { flexDirection: rtl.rowDirection }]}>
        {(['template', 'auto', 'ar', 'en', 'bilingual'] as const).map(value => {
          const active = language === value;
          return (
            <TouchableOpacity disabled={disabled} key={value} style={[styles.language, active && styles.active]} onPress={() => onLanguage(value)} accessibilityRole="radio" accessibilityState={{ checked: active, disabled }}>
              <Text style={[styles.languageText, active && styles.activeText]}>{value === 'template' ? t('documents.picker.templateLanguage') : value === 'auto' ? t('documents.picker.appLanguage') : t(`documents.lang.${value}` as any)}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
      <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t(language === 'template' ? 'documents.picker.templateLanguageHint' : 'documents.picker.languageOverrideHint')}</Text>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  wrap: { gap: spacing.sm },
  label: { fontSize: typography.fontSize.sm, fontWeight: '700', color: colors.text.primary, marginTop: spacing.sm },
  selection: { alignItems: 'center', gap: spacing.sm, padding: spacing.md, borderRadius: 12, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  info: { flex: 1, minWidth: 0, gap: 3 },
  selectionName: { fontSize: typography.fontSize.sm, fontWeight: '600', color: colors.text.primary },
  hint: { fontSize: typography.fontSize.xs, lineHeight: 19, color: colors.text.secondary },
  link: { fontSize: typography.fontSize.sm, fontWeight: '700', color: colors.primary, paddingVertical: spacing.xs },
  choices: { gap: spacing.xs },
  option: { alignItems: 'center', gap: spacing.sm, padding: spacing.sm, minHeight: 44, borderRadius: 8, borderWidth: 1, borderColor: colors.border },
  optionText: { flex: 1, fontSize: typography.fontSize.sm, color: colors.text.primary },
  languages: { flexWrap: 'wrap', gap: spacing.xs },
  language: { minHeight: 40, paddingVertical: spacing.sm, paddingHorizontal: spacing.md, borderRadius: 10, borderWidth: 1, borderColor: colors.border, justifyContent: 'center' },
  languageText: { fontSize: typography.fontSize.xs, color: colors.text.primary, fontWeight: '600' },
  active: { backgroundColor: withAlpha(colors.primary, 0.08), borderColor: colors.primary },
  activeText: { color: colors.primary },
  errorBox: { gap: spacing.xs, padding: spacing.sm, borderWidth: 1, borderColor: colors.border, borderRadius: 10 },
});
